import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Tags, Scale, Calculator, Save, Loader2, Info
} from 'lucide-react';
import {
  getCustomerProductMapping, updateFormula
} from '../lib/api';

export default function CustomerProductFormulaPage() {
  const navigate = useNavigate();
  const { mappingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(null);
  const [activeTab, setActiveTab] = useState('formula');

  // Formula form state
  const [unitWeightKg, setUnitWeightKg] = useState('');
  const [unitsPerCase, setUnitsPerCase] = useState('');
  const [saving, setSaving] = useState(false);

  // Live calculator test values
  const [testWeight, setTestWeight] = useState('');
  const [testUnits, setTestUnits] = useState('');
  const [testCases, setTestCases] = useState('');
  const [calcSource, setCalcSource] = useState(null); // which field was last typed

  useEffect(() => {
    loadMapping();
  }, [mappingId]);

  const loadMapping = async () => {
    setLoading(true);
    try {
      const { data } = await getCustomerProductMapping(mappingId);
      setMapping(data);
      if (data.unit_weight_kg) setUnitWeightKg(data.unit_weight_kg.toString());
      if (data.units_per_case) setUnitsPerCase(data.units_per_case.toString());
    } catch {
      toast.error('Failed to load mapping');
      navigate('/supplier-codes');
    } finally {
      setLoading(false);
    }
  };

  // Derived formula values
  const uwkg = parseFloat(unitWeightKg) || 0;
  const upc = parseInt(unitsPerCase) || 0;
  const caseWeightKg = uwkg > 0 && upc > 0 ? (uwkg * upc).toFixed(2) : ',';
  const hasFormula = uwkg > 0 && upc > 0;

  // Live calculator auto-compute
  const handleCalcChange = useCallback((field, value) => {
    if (field === 'weight') {
      setTestWeight(value);
      setCalcSource('weight');
      if (hasFormula && parseFloat(value) > 0) {
        const units = Math.round(parseFloat(value) / uwkg);
        setTestUnits(units.toString());
        setTestCases(Math.ceil(units / upc).toString());
      }
    } else if (field === 'units') {
      setTestUnits(value);
      setCalcSource('units');
      if (hasFormula && parseInt(value) > 0) {
        const u = parseInt(value);
        setTestWeight((u * uwkg).toFixed(2));
        setTestCases(Math.ceil(u / upc).toString());
      }
    } else if (field === 'cases') {
      setTestCases(value);
      setCalcSource('cases');
      if (hasFormula && parseInt(value) > 0) {
        const c = parseInt(value);
        const units = c * upc;
        setTestUnits(units.toString());
        setTestWeight((units * uwkg).toFixed(2));
      }
    }
  }, [hasFormula, uwkg, upc]);

  const handleSave = async () => {
    if (!hasFormula) {
      toast.error('Both values must be greater than zero');
      return;
    }
    setSaving(true);
    try {
      await updateFormula(mappingId, { unit_weight_kg: uwkg, units_per_case: upc });
      toast.success('Formula saved');
      loadMapping();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!mapping) return null;

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/supplier-codes')} data-testid="btn-back-codes">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Codes
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Tags className="w-5 h-5" />
            {mapping.supplier_product_code}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {mapping.customer_name}, {mapping.product_name}
            {mapping.cobranding_name ? ` (${mapping.cobranding_name})` : ''}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start" data-testid="formula-page-tabs">
          <TabsTrigger value="details" data-testid="tab-code-details">
            <Package className="w-4 h-4 mr-1.5" /> Code Details
          </TabsTrigger>
          <TabsTrigger value="formula" data-testid="tab-formula">
            <Scale className="w-4 h-4 mr-1.5" /> Case / Weight / Unit Formula
          </TabsTrigger>
        </TabsList>

        {/* ── Code Details Tab ── */}
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Customer Product Code Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Customer</Label>
                    <p className="font-medium mt-0.5">{mapping.customer_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Internal Product</Label>
                    <p className="font-medium mt-0.5">{mapping.product_name}</p>
                    {mapping.product_sku && (
                      <Badge variant="outline" className="font-mono text-xs mt-1">{mapping.product_sku}</Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Customer Product Code</Label>
                    <p className="mt-0.5">
                      <Badge className="bg-blue-100 text-blue-800 font-mono text-sm">{mapping.supplier_product_code}</Badge>
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Packaging / Brand</Label>
                    <p className="font-medium mt-0.5">
                      {mapping.cobranding_name || <span className="text-muted-foreground">Standard (no co-branding)</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <p className="mt-0.5">
                      <Badge variant={mapping.is_active ? 'default' : 'secondary'}>
                        {mapping.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Formula Tab ── */}
        <TabsContent value="formula">
          <div className="space-y-5">
            {/* Formula Input Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Scale className="w-4 h-4" /> Conversion Formula
                </CardTitle>
                <CardDescription>
                  Define how weight, units, and cases relate for this product code. This drives auto-fill in Sales Order Entry.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="unit-weight">Weight per Unit (kg)</Label>
                      <Input
                        id="unit-weight"
                        type="number"
                        step="0.001"
                        min="0.001"
                        value={unitWeightKg}
                        onChange={(e) => setUnitWeightKg(e.target.value)}
                        placeholder="e.g. 0.5"
                        className="mt-1 font-mono"
                        data-testid="input-unit-weight"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Weight of a single unit in kilograms</p>
                    </div>
                    <div>
                      <Label htmlFor="units-per-case">Units per Case</Label>
                      <Input
                        id="units-per-case"
                        type="number"
                        step="1"
                        min="1"
                        value={unitsPerCase}
                        onChange={(e) => setUnitsPerCase(e.target.value)}
                        placeholder="e.g. 40"
                        className="mt-1 font-mono"
                        data-testid="input-units-per-case"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Number of units that fit in one case</p>
                    </div>
                  </div>

                  {/* Live Formula Summary */}
                  <div className="flex items-center justify-center">
                    <div className={`rounded-xl border-2 p-6 text-center w-full transition-colors ${hasFormula ? 'border-emerald-300 bg-emerald-50/50' : 'border-zinc-200 bg-zinc-50'}`}>
                      {hasFormula ? (
                        <div className="space-y-3">
                          <p className="text-sm font-semibold text-emerald-700">Formula Active</p>
                          <div className="space-y-1.5">
                            <p className="text-lg font-bold text-emerald-900">1 case = {upc} units = {caseWeightKg} kg</p>
                            <p className="text-sm text-emerald-600">1 unit = {uwkg} kg</p>
                          </div>
                          {mapping.formula_source && (
                            <Badge variant="outline" className="text-xs">
                              Source: {mapping.formula_source === 'learned' ? 'Learned from order' : 'Manual'}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Info className="w-8 h-8 text-zinc-400 mx-auto" />
                          <p className="text-sm text-zinc-500">Enter both values to see the formula preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6 pt-4 border-t">
                  <Button onClick={handleSave} disabled={saving || !hasFormula} data-testid="btn-save-formula">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Formula
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Live Calculator */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="w-4 h-4" /> Test Calculator
                </CardTitle>
                <CardDescription>
                  Enter any one value to see the other two computed live. {!hasFormula && '(Set formula above first)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={testWeight}
                      onChange={(e) => handleCalcChange('weight', e.target.value)}
                      placeholder="0.00"
                      disabled={!hasFormula}
                      className={`mt-1 font-mono ${calcSource === 'weight' ? 'ring-2 ring-emerald-300' : ''}`}
                      data-testid="calc-weight"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Units</Label>
                    <Input
                      type="number"
                      step="1"
                      value={testUnits}
                      onChange={(e) => handleCalcChange('units', e.target.value)}
                      placeholder="0"
                      disabled={!hasFormula}
                      className={`mt-1 font-mono ${calcSource === 'units' ? 'ring-2 ring-emerald-300' : ''}`}
                      data-testid="calc-units"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Cases</Label>
                    <Input
                      type="number"
                      step="1"
                      value={testCases}
                      onChange={(e) => handleCalcChange('cases', e.target.value)}
                      placeholder="0"
                      disabled={!hasFormula}
                      className={`mt-1 font-mono ${calcSource === 'cases' ? 'ring-2 ring-emerald-300' : ''}`}
                      data-testid="calc-cases"
                    />
                  </div>
                </div>
                {hasFormula && testWeight && testUnits && testCases && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800" data-testid="calc-result">
                    <strong>{testWeight} kg</strong> = <strong>{testUnits} units</strong> = <strong>{testCases} case{parseInt(testCases) !== 1 ? 's' : ''}</strong>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
